/**
 * Square cross-fading photo gallery for variety product pages.
 *
 * Rendered by snippets/product-media-gallery-content.liquid for products that
 * are varieties. Deliberately dependency-free: no importmap entry, no theme
 * base class, so a slow or failed module graph can never leave the page with
 * an invisible first photo. Without JS the markup already shows photo one.
 *
 * Rules it enforces:
 * - auto-advance is slow (dwell comes from data-hold-ms) and only runs when
 *   data-autorotate is set and the visitor has not asked for reduced motion;
 * - hover, focus, a hidden tab and being scrolled out of view all pause it;
 * - any manual move (arrow, arrow key, swipe) stops rotation for good.
 */

const MIN_HOLD_MS = 2000;
const DEFAULT_HOLD_MS = 5000;
const SWIPE_THRESHOLD_PX = 40;

/**
 * Taking control holds for THIS PAGE only. A session-wide flag would leave
 * every later variety static until the tab closes, wrong when someone is
 * browsing several. Each page gets a fresh rotation; the visitor's choice
 * sticks while they are on that variety.
 */
function readManualFlag() {
  return false;
}

function writeManualFlag() {
  // Intentionally not persisted. See above.
}

class VarietyGallery extends HTMLElement {
  #controller = null;
  #timer = null;
  #observer = null;
  #taken = false;
  #hovered = false;
  #focused = false;
  #offscreen = false;
  #touchX = null;

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    const slides = this.#slides();
    if (slides.length < 2) return;

    if (readManualFlag()) this.#taken = true;

    this.#bind('[data-variety-prev]', () => this.#step(-1), signal);
    this.#bind('[data-variety-next]', () => this.#step(1), signal);

    this.addEventListener('keydown', this.#onKeydown, { signal });
    this.addEventListener('pointerenter', () => this.#setPaused('hover', true), { signal });
    this.addEventListener('pointerleave', () => this.#setPaused('hover', false), { signal });
    this.addEventListener('focusin', () => this.#setPaused('focus', true), { signal });
    this.addEventListener('focusout', () => this.#setPaused('focus', false), { signal });
    this.addEventListener('touchstart', this.#onTouchStart, { signal, passive: true });
    this.addEventListener('touchend', this.#onTouchEnd, { signal, passive: true });
    document.addEventListener('visibilitychange', this.#onVisibility, { signal });

    if (typeof IntersectionObserver === 'function') {
      this.#observer = new IntersectionObserver(
        ([entry]) => {
          this.#offscreen = !entry.isIntersecting;
          this.#restart();
        },
        { threshold: 0.15 }
      );
      this.#observer.observe(this);
    }

    this.#restart();
  }

  disconnectedCallback() {
    this.#stopTimer();
    this.#observer?.disconnect();
    this.#observer = null;
    this.#controller?.abort();
    this.#controller = null;
  }

  /** Slides are re-read on every use so a section morph cannot desync us. */
  #slides() {
    return Array.from(this.querySelectorAll('[data-variety-slide]'));
  }

  #bind(selector, handler, signal) {
    const button = this.querySelector(selector);
    if (!button) return;
    button.addEventListener(
      'click',
      (event) => {
        event.preventDefault();
        handler();
      },
      { signal }
    );
  }

  get #holdMs() {
    const parsed = parseInt(this.dataset.holdMs || '', 10);
    if (Number.isNaN(parsed)) return DEFAULT_HOLD_MS;
    return Math.max(MIN_HOLD_MS, parsed);
  }

  get #canRotate() {
    if (this.#taken) return false;
    if (this.dataset.autorotate !== 'true') return false;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return false;
    return this.#slides().length > 1;
  }

  #setPaused(reason, value) {
    if (reason === 'hover') this.#hovered = value;
    if (reason === 'focus') this.#focused = value;
    this.#restart();
  }

  #onVisibility = () => this.#restart();

  #onKeydown = (event) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.#step(-1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.#step(1);
    }
  };

  #onTouchStart = (event) => {
    this.#touchX = event.changedTouches?.[0]?.clientX ?? null;
  };

  #onTouchEnd = (event) => {
    if (this.#touchX == null) return;
    const endX = event.changedTouches?.[0]?.clientX ?? null;
    const startX = this.#touchX;
    this.#touchX = null;
    if (endX == null) return;
    const distance = endX - startX;
    if (Math.abs(distance) < SWIPE_THRESHOLD_PX) return;
    this.#step(distance < 0 ? 1 : -1);
  };

  /** A manual move hands control to the visitor and keeps it there. */
  #step(direction) {
    this.#taken = true;
    writeManualFlag();
    this.#stopTimer();
    this.#go(direction, true);
  }

  #go(direction, announce) {
    const slides = this.#slides();
    if (slides.length < 2) return;

    let current = slides.findIndex((slide) => slide.classList.contains('is-active'));
    if (current < 0) current = 0;

    const next = (current + direction + slides.length) % slides.length;
    if (next === current) return;

    slides.forEach((slide, index) => {
      const active = index === next;
      slide.classList.toggle('is-active', active);
      if (active) {
        slide.removeAttribute('aria-hidden');
        slide.removeAttribute('inert');
      } else {
        slide.setAttribute('aria-hidden', 'true');
        slide.setAttribute('inert', '');
      }
    });

    if (announce) {
      const status = this.querySelector('[data-variety-status]');
      if (status) status.textContent = `Photo ${next + 1} of ${slides.length}`;
    }
  }

  #restart() {
    this.#stopTimer();
    if (!this.#canRotate) return;
    if (this.#hovered || this.#focused || this.#offscreen || document.hidden) return;
    this.#timer = window.setInterval(() => {
      if (document.hidden) return;
      this.#go(1, false);
    }, this.#holdMs);
  }

  #stopTimer() {
    if (this.#timer != null) {
      window.clearInterval(this.#timer);
      this.#timer = null;
    }
  }
}

if (!customElements.get('variety-gallery')) {
  customElements.define('variety-gallery', VarietyGallery);
}
